import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import Home from "@/pages/Home";
import Reports from "@/pages/Reports";
import ReportDetail from "@/pages/ReportDetail";
import Methodology from "@/pages/Methodology";
import DisclosurePolicy from "@/pages/DisclosurePolicy";
import CrawlerPolicy from "@/pages/CrawlerPolicy";
import Appeal from "@/pages/Appeal";
import ReportServer from "@/pages/ReportServer";
import Check from "@/pages/Check";
import ApiOverview from "@/pages/ApiOverview";
import EarlyAccess from "@/pages/EarlyAccess";
import Privacy from "@/pages/Privacy";
import GetKey from "@/pages/GetKey";
import Watchlist from "@/pages/Watchlist";
import Unsubscribe from "@/pages/Unsubscribe";
import Servers from "@/pages/Servers";
import ServerProfile from "@/pages/ServerProfile";
import AdminApp from "@/admin/AdminApp";
import AdminErrorBoundary, { AppErrorBoundary } from "@/admin/AdminErrorBoundary";
import Logo from "@/components/brand/Logo";
import ThemeToggle from "@/components/brand/ThemeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export { axios };

const NAV = [
  ["/", "Home", "nav-home"], ["/servers", "Servers", "nav-servers"], ["/check", "Check", "nav-check"], ["/reports", "Reports", "nav-reports"],
  ["/methodology", "Methodology", "nav-methodology"], ["/disclosure-policy", "Disclosure", "nav-disclosure"],
  ["/crawler-policy", "Crawler", "nav-crawler"], ["/report", "Report a server", "nav-report"],
  ["/appeal", "Appeal", "nav-appeal"], ["/developers", "API Docs", "nav-api-docs"],
];
// /admin is deliberately not linked from the public site. It stays reachable directly; it sits behind
// Cloudflare Access, so any link to it must be a plain <a href="/admin"> (full page navigation), never <Link>.

function PublicNav() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  const isActive = (to) => loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
  const links = (prefix) => NAV.map(([to, label, testid]) => (
    <Link key={to} to={to} data-testid={prefix ? `${prefix}-${testid}` : testid} className="hs-nav-link"
          aria-current={isActive(to) ? "page" : undefined}>{label}</Link>
  ));
  return (
    <header className="hs-nav">
      <nav data-testid="site-nav" aria-label="Main" className="hs-container">
        <div className="hs-nav-inner">
          <Link to="/" data-testid="brand-link" className="hs-nav-logo" aria-label="HerdSafe home">
            <span className="hs-logo-full"><Logo variant="horizontal" alt="HerdSafe" /></span>
            <span className="hs-logo-mark"><Logo variant="mark" alt="HerdSafe" /></span>
          </Link>
          <div className="hs-nav-links">{links("")}<ThemeToggle /></div>
          <div className="hs-nav-toggle">
            <ThemeToggle />
            <button type="button" className="hs-icon-btn" data-testid="nav-menu-btn" aria-expanded={open}
                    aria-controls="site-menu" onClick={() => setOpen(!open)}>{open ? "Close" : "Menu"}</button>
          </div>
        </div>
        {open && <div id="site-menu" className="hs-nav-menu" data-testid="site-menu">{links("menu")}</div>}
      </nav>
    </header>
  );
}

function PublicShell({ children }) {
  const year = new Date().getFullYear();
  return (
    <div className="min-h-screen bg-bg text-fg flex flex-col">
      <PublicNav />
      <main className="hs-container w-full flex-1 py-12 md:py-16" style={{ boxSizing: "border-box" }}>{children}</main>
      <footer data-testid="site-footer" className="border-t border-border">
        <div className="hs-container flex flex-wrap items-center gap-4 py-6 text-sm text-muted">
          <span>© {year} HerdSafe. All rights reserved.</span>
          <a data-testid="footer-contact" href="mailto:support@herdsafe.ai" className="hs-link">support@herdsafe.ai</a>
          <a data-testid="footer-security" href="mailto:security@herdsafe.ai" className="hs-link">security@herdsafe.ai</a>
          <Link data-testid="footer-get-key" to="/get-key" className="hs-link">Free API key</Link>
          <Link data-testid="footer-watchlist" to="/watchlist" className="hs-link">Watchlist</Link>
          <Link data-testid="footer-privacy" to="/privacy" className="hs-link">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}

function PublicRoutes() {
  return (
    <PublicShell>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/check" element={<Check />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/reports/:period" element={<ReportDetail />} />
        <Route path="/methodology" element={<Methodology />} />
        <Route path="/disclosure-policy" element={<DisclosurePolicy />} />
        <Route path="/crawler-policy" element={<CrawlerPolicy />} />
        <Route path="/report" element={<ReportServer />} />
        <Route path="/appeal" element={<Appeal />} />
        <Route path="/developers" element={<ApiOverview />} />
        <Route path="/early-access" element={<EarlyAccess />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/get-key" element={<GetKey />} />
        <Route path="/watchlist" element={<Watchlist />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/servers" element={<Servers />} />
        <Route path="/servers/:slug" element={<ServerProfile />} />
      </Routes>
    </PublicShell>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin/*" element={<AdminErrorBoundary name="Admin"><AdminApp /></AdminErrorBoundary>} />
        <Route path="/*" element={<AppErrorBoundary name="Public app"><PublicRoutes /></AppErrorBoundary>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
